import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { useSubscription } from "@/hooks/use-subscription";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Zap, Send, History, Eye, MessageSquare, Users, Sparkles, Clock, Trash2, AlertTriangle } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { AccessGatedAction } from "@/components/AccessGatedAction";
import {
  previewFlashSaleAudienceFn,
  sendFlashSaleSMSFn,
} from "@/lib/flash-sale.functions";
import { getSeSmsNumberFn } from "@/lib/sms-config.functions";
import { filterMerchantBody } from "@/lib/sms/body-filter";
import { calculateSmsSegments } from "@/lib/sms/segments";
import { sanitizeToGsm7 } from "@/lib/sms/gsm7";


import { useLocalizedPrices } from "@/hooks/use-localized-prices";
import { getMerchantUsage, type UsageInfo } from "@/lib/usage.functions";
import { withAuth } from "@/lib/server-fn-auth";

export const Route = createFileRoute("/_authenticated/flash-sale")({
  component: FlashSalePage,
});

type FlashSale = Tables<"flash_sales">;

type DurationChoice = "today" | 24 | 48 | 72;
const DURATION_OPTIONS: DurationChoice[] = ["today", 24, 48, 72];

// GSM-7 sanitizer lives in src/lib/sms/gsm7.ts and is applied both here (UX)
// and server-side in src/lib/sms/flash-sale-sms.server.ts, so the
// single-segment / 1-credit invariant holds end-to-end.
const SMS_LIMIT = 160;



function FlashSalePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { current } = useCurrentLocation();
  const { tier, loading: subLoading } = useSubscription();
  const [merchant, setMerchant] = useState<Tables<"merchants"> | null>(null);
  const [loading, setLoading] = useState(true);

  const canUseFlashSale = tier === "standard" || tier === "growth" || tier === "enterprise";

  const [flashPercent, setFlashPercent] = useState(15);
  const [minWeeks, setMinWeeks] = useState(8);
  const [maxRecipients, setMaxRecipients] = useState(50);
  const [durationChoice, setDurationChoice] = useState<DurationChoice>(48);
  const [messageBody, setMessageBody] = useState("");
  const [sanitizedHint, setSanitizedHint] = useState(false);
  const [urlBlockedHint, setUrlBlockedHint] = useState(false);
  const [seNumber, setSeNumber] = useState<string>("");

  const [sending, setSending] = useState(false);
  // Interactive paywall preview knobs (Solo tier only).
  const [previewPct, setPreviewPct] = useState(15);
  const [previewBody, setPreviewBody] = useState("Vi saknar dig!");
  const [previewHours, setPreviewHours] = useState(48);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewCapped, setPreviewCapped] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [pendingSaleId, setPendingSaleId] = useState<string | null>(null);

  // Credit position for the send pre-flight (only needed at confirm time).
  useEffect(() => {
    if (!confirmOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const u = await withAuth(getMerchantUsage)();
        if (!cancelled) setUsage(u);
      } catch {
        // Non-critical — the send path tops up / gates on its own.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [confirmOpen]);

  // Remaining credits (plan cap + top-up ledger − used). Null = unlimited or
  // unknown, in which case we show no shortfall warning.
  const creditsRemaining =
    usage && usage.effectiveCap !== null ? Math.max(0, usage.effectiveCap - usage.scans) : null;
  const creditShortfall =
    creditsRemaining !== null && previewCapped != null
      ? Math.max(0, previewCapped - creditsRemaining)
      : 0;


  const [pastSales, setPastSales] = useState<FlashSale[]>([]);

  // For the Solo→Standard paywall: fetch localized monthly prices so we can
  // show the real per-month upgrade delta (SEK for Swedish visitors, USD otherwise).
  const priceMap = useLocalizedPrices(["tack_starter_monthly", "tack_standard_monthly"]);
  const upgradeDeltaText = (() => {
    const solo = priceMap?.["tack_starter_monthly"];
    const std = priceMap?.["tack_standard_monthly"];
    if (!solo || !std) return null;
    const soloMinor = Number(solo.amountMinor);
    const stdMinor = Number(std.amountMinor);
    if (!Number.isFinite(soloMinor) || !Number.isFinite(stdMinor)) return null;
    const diff = (stdMinor - soloMinor) / 100;
    if (diff <= 0) return null;
    try {
      const locale = typeof navigator !== "undefined" ? navigator.language : "en-US";
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: std.currency,
        maximumFractionDigits: 0,
      }).format(diff);
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (!user || !current?.id) return;
    setLoading(true);
    supabase
      .from("merchants")
      .select("*")
      .eq("id", current.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setMerchant(data);
        setLoading(false);
      });
  }, [user, current?.id]);

  useEffect(() => {
    if (!merchant) return;
    supabase
      .from("flash_sales")
      .select("*")
      .eq("merchant_id", merchant.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data) setPastSales(data);
      });
  }, [merchant]);

  // Fetch the real Swedish SMS long number once so the preview footer matches
  // exactly what the server appends (see appendComplianceFooter in gateway.server.ts).
  useEffect(() => {
    let cancelled = false;
    getSeSmsNumberFn()
      .then((r) => {
        if (!cancelled && r?.number) setSeNumber(r.number);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const businessName = (merchant?.business_name || "").trim();
  // Server slices the name to 25 chars in the outgoing SMS; mirror here so the
  // char counter and phone preview match what actually gets sent.
  const businessNameForSms = businessName.slice(0, 25);
  const optoutCode = (merchant?.sms_optout_code || "").trim();
  // Mirror the server gate (flash-sale-sms.server.ts): a booking link only
  // counts once it has been approved, otherwise the SMS says "Kod:".
  const hasBookingUrl = !!(
    merchant?.booking_url &&
    merchant.booking_url.trim() &&
    merchant.booking_url_status === "approved"
  );
  const linkLabel = hasBookingUrl ? t("flashSale.suffixBook") : t("flashSale.suffixCode");

  // Auto lead-in: "<pct>% rabatt hos <merchant>! " — merchant name up-front so
  // the offer + brand hit the customer immediately.
  const leadIn = businessNameForSms
    ? `${flashPercent}% ${t("flashSale.leadIn")} ${businessNameForSms}!\n\n`
    : "";

  // Auto-suffix the merchant sees in the preview. Matches the real server
  // template exactly: "Kod: tack.company/r/<12>. Giltig {h}h.\nAvreg: ..."
  // We use a placeholder token of the same length as the real one so the
  // char counter reflects reality.
  const tokenPlaceholder = "XXXXXXXXXXXX"; // 12 chars, matches server token
  const linkPreview = `tack.company/r/${tokenPlaceholder}`;

  // Real STOP footer: "Avreg: STOP<code> <number>" — same format the server
  // emits. Kept as its own line so it visually separates from the offer.
  const stopFooter =
    optoutCode && seNumber
      ? `${t("flashSale.suffixOptOut")} STOP${optoutCode} ${seNumber}`
      : `${t("flashSale.suffixOptOut")} STOP${optoutCode || "XXXX"}`;

  const isSameDay = durationChoice === "today";
  const durationHours = isSameDay ? 24 : (durationChoice as number);
  const validityText = isSameDay
    ? t("flashSale.validityToday")
    : t("flashSale.validity", { hours: durationHours });
  const autoSuffix = `\n\n${linkLabel} ${linkPreview} - ${validityText}\n\n${stopFooter}`;

  const fullPreview = `${leadIn}${messageBody}${autoSuffix}`;
  // Carrier billing is per segment, and GSM-7 extended chars ({ } [ ] ~ \ | ^ €)
  // cost two septets each — so price the preview in segments, not characters.
  const segInfo = calculateSmsSegments(fullPreview);
  const usedChars = segInfo.units;
  const creditCost = segInfo.segments;
  const overLimit = creditCost > 1;
  const bodyEmpty = messageBody.trim().length === 0;
  const bodyMax = Math.min(30, Math.max(0, SMS_LIMIT - leadIn.length - autoSuffix.length));


  useEffect(() => {
    if (!sanitizedHint) return;
    const to = setTimeout(() => setSanitizedHint(false), 3000);
    return () => clearTimeout(to);
  }, [sanitizedHint, messageBody]);

  useEffect(() => {
    if (!urlBlockedHint) return;
    const to = setTimeout(() => setUrlBlockedHint(false), 4000);
    return () => clearTimeout(to);
  }, [urlBlockedHint, messageBody]);

  const onMessageChange = (raw: string) => {
    const gsm = sanitizeToGsm7(raw);
    const filtered = filterMerchantBody(gsm.text);
    setMessageBody(filtered.text.slice(0, bodyMax));
    if (gsm.changed || filtered.text.length > bodyMax) setSanitizedHint(true);
    if (filtered.changed) setUrlBlockedHint(true);
  };


  const handlePrepare = async () => {
    if (!merchant) return;
    setPreviewLoading(true);
    setPreviewCount(null);
    setPreviewCapped(null);
    setConfirmOpen(true);

    let expiresAt: Date;
    if (isSameDay) {
      // Local end-of-day (browser tz — Sweden merchants set to Europe/Stockholm).
      expiresAt = new Date();
      expiresAt.setHours(23, 59, 59, 999);
    } else {
      expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + durationHours);
    }
    const composedHeadline = `${leadIn}${messageBody.trim()}`;
    const { data: draft } = await supabase
      .from("flash_sales")
      .insert({
        merchant_id: merchant.id,
        headline: composedHeadline,
        body_text: messageBody.trim() || null,
        flash_discount_percent: flashPercent,
        upgrade_percent: flashPercent,
        new_code_percent: flashPercent,
        duration_hours: durationHours,
        same_day: isSameDay,
        expires_at: expiresAt.toISOString(),
        audience_min_weeks_since_last_visit: minWeeks,
        max_recipients: Math.min(1000, Math.max(1, maxRecipients || 1)),
        status: "draft",
      } as any)
      .select()
      .single();

    if (!draft) {
      toast.error(t("flashSale.toastFailedCreate"));
      setConfirmOpen(false);
      setPreviewLoading(false);
      return;
    }

    setPendingSaleId(draft.id);
    const preview = await previewFlashSaleAudienceFn({ data: { flashSaleId: draft.id } });
    setPreviewCount(preview.ok ? preview.eligible : 0);
    setPreviewCapped(preview.ok ? preview.capped : 0);
    setPreviewLoading(false);
  };

  const handleCancelConfirm = async () => {
    setConfirmOpen(false);
    if (pendingSaleId) {
      await supabase.from("flash_sales").delete().eq("id", pendingSaleId);
      setPendingSaleId(null);
    }
  };

  const handleDeletePastSale = async (sale: FlashSale) => {
    const isDraft = sale.status === "draft";
    if (!isDraft) {
      const ok = window.confirm(t("flashSale.deleteConfirm"));
      if (!ok) return;
    }
    const { error } = await supabase.from("flash_sales").delete().eq("id", sale.id);
    if (error) {
      toast.error(t("flashSale.deleteFailed"));
      return;
    }
    setPastSales((prev) => prev.filter((s) => s.id !== sale.id));
  };

  const handleSend = async () => {
    if (!merchant || !pendingSaleId) return;
    setSending(true);
    setConfirmOpen(false);

    await supabase
      .from("flash_sales")
      .update({ status: "active" })
      .eq("id", pendingSaleId);

    try {
      const result = await sendFlashSaleSMSFn({ data: { flashSaleId: pendingSaleId } });
      if (!result?.ok) throw new Error(result?.error || "Send failed");
      toast.success(t("flashSale.toastCreated", { n: result.sent || 0 }));
      const { data: updated } = await supabase
        .from("flash_sales")
        .select("*")
        .eq("merchant_id", merchant.id)
        .order("created_at", { ascending: false });
      if (updated) setPastSales(updated);
      setMessageBody("");
    } catch {
      toast.error(t("flashSale.toastFailedSend"));
    }
    setPendingSaleId(null);
    setSending(false);
  };

  if (loading || subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("flashSale.loading")}</div>
      </div>
    );
  }

  // ---------- Paywall (Solo) ----------
  if (!canUseFlashSale) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 py-10 space-y-6">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">
              <Sparkles size={14} /> {t("flashSale.gate.badge")}
            </div>
            <h1 className="text-3xl md:text-4xl font-display font-bold">
              {t("flashSale.gate.hero")}
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t("flashSale.gate.sub")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-start">
            <Card className="border-primary/30">
              <CardContent className="p-6 space-y-4">
                <FeatureRow
                  icon={<MessageSquare size={18} />}
                  title={t("flashSale.gate.bullet1Title")}
                  body={t("flashSale.gate.bullet1Body")}
                />
                <FeatureRow
                  icon={<Clock size={18} />}
                  title={t("flashSale.gate.bullet2Title")}
                  body={t("flashSale.gate.bullet2Body")}
                />
                <FeatureRow
                  icon={<Users size={18} />}
                  title={t("flashSale.gate.bullet3Title")}
                  body={t("flashSale.gate.bullet3Body")}
                />
                <div className="pt-2 space-y-1.5">
                  <div className="flex flex-wrap gap-2">
                    <Button asChild>
                      <Link to="/settings" search={{ tab: "billing" } as any}>
                        {t("flashSale.gate.cta")}
                      </Link>
                    </Button>
                    <Button asChild variant="ghost">
                      <Link to="/why-tack">{t("flashSale.gate.secondary")}</Link>
                    </Button>
                  </div>
                  {upgradeDeltaText && (
                    <p className="text-xs text-muted-foreground">
                      {t("flashSale.gate.upgradeDelta", { amount: upgradeDeltaText })}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <div>
              <PhoneMock
                text={buildFlashSampleSms({
                  businessName: businessNameForSms || "Mitt företag",
                  discountPct: previewPct,
                  durationHours: previewHours,
                  hasBookingUrl,
                  optoutCode: optoutCode || "LXGP",
                  seNumber: seNumber || "+46790953786",
                  bodyOverride: previewBody || "Vi saknar dig!",
                })}
                header={t("flashSale.previewPhoneHeader")}
              />
            </div>

            <div className="rounded-2xl border border-primary/40 bg-card p-4 shadow-sm">
              <div className="mb-3">
                <div className="text-sm font-semibold text-primary">Testa förhandsvisningen</div>
                <div className="text-xs text-muted-foreground">Ändra värden och se SMS:et uppdateras direkt.</div>
              </div>
              <div className="flex flex-col gap-3">
                <div className="space-y-1">
                  <Label htmlFor="previewPct" className="text-xs text-muted-foreground">
                    % rabatt
                  </Label>

                    <div className="flex items-center gap-1">
                      <Input
                        id="previewPct"
                        type="number"
                        min={1}
                        max={100}
                        value={previewPct}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n)) setPreviewPct(Math.max(1, Math.min(100, n)));
                        }}
                        className="h-8 w-full"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="previewBody" className="text-xs text-muted-foreground flex items-center justify-between">
                      <span>Text</span>
                      <span className="tabular-nums">{previewBody.length}/30</span>
                    </Label>
                    <Input
                      id="previewBody"
                      value={previewBody}
                      maxLength={30}
                      onChange={(e) => {
                        const gsm = sanitizeToGsm7(e.target.value);
                        setPreviewBody(gsm.text.slice(0, 30));
                      }}
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Giltighetstid</Label>
                    <div className="flex gap-1">
                      {[24, 48, 72].map((h) => (
                        <button
                          key={h}
                          type="button"
                          onClick={() => setPreviewHours(h)}
                          className={`h-8 flex-1 rounded-md border text-xs font-medium transition-colors ${
                            previewHours === h
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input bg-background hover:bg-accent"
                          }`}
                        >
                          {h}h
                        </button>
                      ))}
                    </div>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

  }

  // ---------- Composer (Standard+) ----------
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-xl font-display font-bold">{t("flashSale.title")}</h1>

        <div className="grid md:grid-cols-[1fr_300px] gap-6 items-start">
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Zap size={18} /> {t("flashSale.create")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="flashPercent">{t("flashSale.discountPct")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("flashSale.discountPctHint")}
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="flashPercent"
                    type="number"
                    min={1}
                    max={100}
                    value={flashPercent}
                    onChange={(e) => setFlashPercent(Number(e.target.value))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="minWeeks">{t("flashSale.minWeeks")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("flashSale.minWeeksHint")}
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="minWeeks"
                    type="number"
                    min={1}
                    max={52}
                    value={minWeeks}
                    onChange={(e) => setMinWeeks(Number(e.target.value))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">{t("flashSale.weeks")}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxRecipients">{t("flashSale.maxRecipients")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("flashSale.maxRecipientsHint")}
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="maxRecipients"
                    type="number"
                    min={1}
                    max={1000}
                    value={maxRecipients}
                    onChange={(e) => setMaxRecipients(Number(e.target.value))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">
                    {t("flashSale.maxRecipientsUnit")}
                  </span>
                </div>
              </div>
            </div>


            <div className="space-y-2">
              <Label className="text-sm font-medium">{t("flashSale.duration")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("flashSale.durationHint")}
              </p>
              <div className="flex gap-1.5">
                {DURATION_OPTIONS.map((d) => (
                  <button
                    key={String(d)}
                    onClick={() => setDurationChoice(d)}
                    className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                      durationChoice === d
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-foreground border-border hover:bg-muted"
                    }`}
                  >
                    {d === "today" ? t("flashSale.durationToday") : `${d}h`}
                  </button>
                ))}
              </div>
            </div>

            {/* SMS composer */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="messageBody">{t("flashSale.messageLabel")}</Label>
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                  {t("flashSale.singleSmsBadge")}
                </span>
              </div>

              <div
                className={`rounded-lg border bg-background overflow-hidden ${
                  overLimit ? "border-destructive" : "border-input"
                }`}
              >
                <Textarea
                  id="messageBody"
                  value={messageBody}
                  onChange={(e) => onMessageChange(e.target.value)}
                  maxLength={bodyMax}
                  placeholder={t("flashSale.messagePlaceholder")}
                  className="min-h-[70px] resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
                />
                <div className="px-3 pb-2 pt-1 text-xs text-muted-foreground border-t bg-muted/30">
                  <div className="font-medium mb-0.5">{t("flashSale.autoSuffixNote")}</div>
                  <div className="font-mono text-[11px] break-words">{autoSuffix.trim()}</div>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span
                  className={
                    overLimit ? "text-destructive font-medium" : "text-muted-foreground"
                  }
                >
                  {t("flashSale.charsUsed", { used: usedChars })}
                  {" · "}
                  {t("flashSale.creditCost", { count: creditCost })}
                </span>
                {urlBlockedHint ? (
                  <span className="text-destructive font-medium">
                    {t("flashSale.urlBlockedNote")}
                  </span>
                ) : sanitizedHint ? (
                  <span className="text-muted-foreground">
                    {t("flashSale.charsSanitizedNote")}
                  </span>
                ) : null}
              </div>
              {overLimit && (
                <p className="text-xs text-destructive">
                  {t("flashSale.creditCostWarning", { count: creditCost })}
                </p>
              )}

            </div>



            <AccessGatedAction>
              <Button
                onClick={handlePrepare}
                disabled={sending || bodyEmpty || overLimit || !businessName}
                className="w-full sm:w-auto"
              >
                <Eye className="size-4 mr-2" />
                {sending ? t("flashSale.sending") : t("flashSale.reviewCta")}
              </Button>
            </AccessGatedAction>
          </CardContent>
        </Card>

        <div className="md:sticky md:top-6 space-y-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
            {t("flashSale.livePreviewLabel")}
          </div>
          <PhoneMock
            header={t("flashSale.previewPhoneHeader")}
            text={fullPreview}
          />
        </div>
        </div>

        {pastSales.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <History size={18} /> {t("flashSale.past")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pastSales.map((sale) => (
                  <div
                    key={sale.id}
                    className="flex items-center justify-between rounded-lg border p-3 text-sm"
                  >
                    <div className="space-y-0.5 min-w-0">
                      <p className="font-medium truncate max-w-[300px]">{sale.headline}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(sale.created_at!).toLocaleDateString()} ·{" "}
                        {sale.duration_hours}h ·{" "}
                        {sale.flash_discount_percent ?? sale.new_code_percent}% off
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 pl-3">
                      <div className="text-right">
                        <p className="font-semibold">
                          {t("flashSale.sent", { n: sale.emails_sent })}
                        </p>
                        <p
                          className={`text-xs ${
                            sale.status === "active"
                              ? "text-green-600"
                              : "text-muted-foreground"
                          }`}
                        >
                          {sale.status}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeletePastSale(sale)}
                        aria-label={t("flashSale.deleteAria")}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Review dialog */}
      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => {
          if (!o) handleCancelConfirm();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("flashSale.previewTitle")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">


            <div className="rounded-lg border p-3 space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <Users size={16} className="mt-0.5 text-muted-foreground shrink-0" />
                <span>
                  {previewLoading
                    ? t("flashSale.previewLoading")
                    : previewCount === 0
                    ? t("flashSale.previewEmpty")
                    : (previewCapped ?? 0) < (previewCount ?? 0)
                    ? t("flashSale.recipientSummaryCapped", {
                        count: previewCount ?? 0,
                        capped: previewCapped ?? 0,
                        weeks: minWeeks,
                      })
                    : t("flashSale.recipientSummary", {
                        count: previewCount ?? 0,
                        weeks: minWeeks,
                      })}
                </span>
              </div>
              {!previewLoading && (previewCapped ?? 0) > 0 && (
                <div className="flex items-start gap-2 text-muted-foreground">
                  <MessageSquare size={16} className="mt-0.5 shrink-0" />
                  <span>
                    {t("flashSale.creditSummary", { count: previewCapped ?? 0 })}
                  </span>
                </div>
              )}
              {creditShortfall > 0 && (
                <div className="flex items-start gap-2 text-destructive">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>
                    {usage?.overageEnabled
                      ? t("flashSale.creditShortfallAutoTopup", {
                          missing: creditShortfall,
                        })
                      : t("flashSale.creditShortfall", {
                          missing: creditShortfall,
                          remaining: creditsRemaining ?? 0,
                        })}
                  </span>
                </div>
              )}
              <div className="flex items-start gap-2 text-muted-foreground">
                <Clock size={16} className="mt-0.5 shrink-0" />
                <span>
                  {isSameDay
                    ? t("flashSale.campaignSummaryToday", { pct: flashPercent })
                    : t("flashSale.campaignSummary", { pct: flashPercent, hours: durationHours })}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelConfirm}>
              {t("flashSale.cancel")}
            </Button>
            <Button
              onClick={handleSend}
              disabled={previewLoading || (previewCount ?? 0) === 0}
            >
              <Send className="size-4 mr-2" /> {t("flashSale.sendNow")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeatureRow({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 rounded-md bg-primary/10 text-primary p-2">{icon}</div>
      <div>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-sm text-muted-foreground">{body}</div>
      </div>
    </div>
  );
}

/** iPhone-style SMS bubble mock. Purely visual — takes exact text. */
function PhoneMock({ text, header }: { text: string; header: string }) {
  return (
    <div className="mx-auto w-full max-w-[280px] rounded-[2rem] border-8 border-foreground/90 bg-foreground/90 shadow-xl">
      <div className="rounded-[1.4rem] bg-[#f2f2f7] overflow-hidden">
        <div className="bg-[#e5e5ea] px-4 py-2 text-center text-[11px] font-medium text-foreground/70">
          {header}
        </div>
        <div className="p-4 min-h-[140px] flex items-start">
          <div className="max-w-[90%] rounded-2xl rounded-bl-md bg-white px-3 py-2 text-[13px] leading-snug text-foreground shadow-sm whitespace-pre-line">
            {text}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Mirrors the exact SMS format the server sends in
 * `src/lib/sms/flash-sale-sms.server.ts` + `appendSwedishFooter`, so the
 * paywall preview is character-identical to the real send.
 */
function buildFlashSampleSms({
  businessName,
  discountPct,
  durationHours,
  hasBookingUrl,
  optoutCode,
  seNumber,
  bodyOverride,
}: {
  businessName: string;
  discountPct: number;
  durationHours: number;
  hasBookingUrl: boolean;
  optoutCode: string;
  seNumber: string;
  bodyOverride?: string;
}): string {
  const linkLabel = hasBookingUrl ? "Boka" : "Kod";
  const validityText = `Giltig ${durationHours}h.`;
  const leadIn = `${discountPct}% rabatt hos ${businessName}!`;
  const merchantBody = bodyOverride || "Vi saknar dig!";
  const suffix = `${linkLabel}: tack.company/r/XXXXXXXXXXXX - ${validityText}`;
  const footer = `Avreg: STOP${optoutCode} ${seNumber}`;
  return `${leadIn}\n\n${merchantBody}\n\n${suffix}\n\n${footer}`;
}

